import { useState } from 'react'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { BankConvertor } from 'utilities/helper/banksConvertor'

/**
 * A registered bank account, with the digits actually on screen.
 *
 * Every manager screen that touches a payout used to render an account as
 * «نام صاحب حساب - بانک ملت» and stop there. That is enough when Hamyan makes
 * the transfer, because nobody has to read the number — but the other half of
 * every اعطای وام screen is «فقط ثبت شود», where the manager pays the borrower
 * themselves. There the card number is the whole point, and the only place it
 * appeared was inside the account-picker modal, two taps away and framed as a
 * question about which account to use rather than as an answer to «به کجا
 * بزنم؟».
 *
 * So: the number, grouped, copyable, next to whose account it is.
 */

/** 4-4-4-4 for a PAN, and IR-xx-xxxx… for a sheba. Never changes a digit. */
export const formatAccountNumber = (value: string) =>
    value
        .replace(/[\s-]/g, '')
        .toUpperCase()
        .replace(/(.{4})(?=.)/g, '$1-')

/**
 * The number a manager would actually transfer to, preferring the card
 * number: a کارت‌به‌کارت is what a fund manager with a phone does, and a sheba
 * needs a banking app and an hour of waiting.
 */
export const accountNumberOf = (account?: IBankAccountCards) => {
    const card = account?.card_number?.replace(/[\s-]/g, '') ?? ''
    if (card) return { value: card, label: 'شمارهٔ کارت', kind: 'card' as const }
    const sheba = (account?.iban || account?.sheba_number || '').replace(
        /[\s-]/g,
        ''
    )
    if (sheba)
        return { value: sheba, label: 'شمارهٔ شبا', kind: 'sheba' as const }
    return undefined
}

export default function BankAccountCard({
    account,
    belongsToBorrower,
    borrowerName,
    onChange,
    source,
}: {
    account?: IBankAccountCards
    /**
     * Whether this account is the borrower's own. `undefined` means the
     * caller cannot tell, and then nothing is claimed either way — a wrong
     * badge here sends real money to the wrong person.
     */
    belongsToBorrower?: boolean
    borrowerName?: string
    /** renders a «تغییر» affordance when the caller can open a picker */
    onChange?: () => void
    /** which screen this rendered on, for analytics */
    source: string
}) {
    const [copied, setCopied] = useState(false)
    const number = accountNumberOf(account)

    if (!account) return null

    const copy = () => {
        if (!number) return
        // `navigator.clipboard` is undefined outside a secure context — plain
        // http:// and a few older in-app webviews. The optional chain then
        // leaves `undefined.then(...)`, which throws a TypeError *before*
        // `.catch()` exists to turn it into the toast below.
        if (!navigator.clipboard) {
            toast.info('شماره را دستی یادداشت کنید')
            return
        }
        navigator.clipboard
            .writeText(number.value)
            .then(() => {
                setCopied(true)
                posthog.capture('bank_account_number_copied', {
                    source,
                    kind: number.kind,
                })
                setTimeout(() => setCopied(false), 2000)
            })
            .catch(() => toast.info('شماره را دستی یادداشت کنید'))
    }

    const logo = BankConvertor[account.bank_unique_code]

    return (
        <div className=" rounded-card bg-sunken p-3">
            <div className=" flex items-center gap-2">
                {logo && (
                    <img src={logo} alt={account.bank_name} className=" h-9 w-9" />
                )}
                <div className=" min-w-0 flex-1">
                    <p className=" truncate text-[13px] font-bold text-heading">
                        {account.full_name || account.owner_name}
                    </p>
                    <p className=" truncate text-[11px] text-text-secondary">
                        {account.bank_name}
                    </p>
                </div>
                {onChange && (
                    <button
                        type="button"
                        onClick={onChange}
                        className=" shrink-0 rounded-full border border-stroke bg-white px-3 py-1 text-[11px] font-bold text-text-body"
                    >
                        تغییر
                    </button>
                )}
            </div>

            {/* Stated only when it is NOT the borrower's account. Silence is
                the common case and the safe one; a manager who deliberately
                picked their own account does not need to be told, but one who
                is about to copy these digits and transfer does. */}
            {belongsToBorrower === false && (
                <p className=" mt-2 rounded-lg bg-warning-soft px-2.5 py-1.5 text-[11px] font-bold leading-5 text-warning-on">
                    این حساب متعلق به {borrowerName || 'گیرندهٔ وام'} نیست.
                </p>
            )}

            {number ? (
                <>
                    <p className=" mt-3 text-[11px] text-text-secondary">
                        {number.label}
                    </p>
                    {/* dir=ltr: a card number is a left-to-right run, and the
                        bidi algorithm otherwise reorders the groups around it.
                        A sheba is 26 characters and wraps on a phone, so it
                        gets a size that wraps once instead of twice. */}
                    <p
                        dir="ltr"
                        className={` mt-0.5 text-left font-bold tracking-wide text-heading ${
                            number.kind === 'card'
                                ? 'text-[17px]'
                                : 'text-[15px]'
                        }`}
                    >
                        {formatAccountNumber(number.value)}
                    </p>
                    <button
                        type="button"
                        onClick={copy}
                        className=" mt-2 w-full rounded-full border border-green-1 bg-white py-2 text-[13px] font-bold text-green-1 transition-transform active:scale-[0.99] motion-reduce:transition-none"
                    >
                        {copied ? 'کپی شد ✓' : `کپی ${number.label}`}
                    </button>
                </>
            ) : (
                <p className=" mt-3 text-[11px] leading-5 text-text-secondary">
                    برای این حساب شماره کارت یا شبایی ثبت نشده است.
                </p>
            )}
        </div>
    )
}
